<template>
    <div class="container-fluid py-3">
        <div class="row mb-3">
            <div class="col-3">
                <form method="post" @submit.prevent="onConnectLedger">
                    <label for="accountIndex">Account Index: (m/44'/60'/0'/0/x)</label>
                    <input type="number" min="0" class="form-control" id="accountIndex" v-model.number="accountIndex" />
                    <button type="submit" class="btn btn-bsc mt-1 w-100">Load from Ledger</button>
                </form>
            </div>
            <div class="col-4">
                <label>Address:</label>
                <p class="bold text-break">{{ bnbAddress }}</p>
            </div>
            <div class="col-3">
                <label>HD Path:</label>
                <p class="bold">{{ hdPath }}</p>
            </div>
            <div class="col-2">
                <button class="btn btn-secondary btn-sm mt-3 mr-1" @click="logout">Logout</button>
                <button class="btn btn-bsc btn-sm mt-3" @click="showAddAddressModal = true">Add Address</button>
                <button class="btn btn-outline-secondary btn-sm mt-2 w-100" @click="showReceive = true" v-if="bnbAddress !== 'N/A'">📥 Receive</button>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-7">
                <div class="total-usdt-banner" v-if="balances.length">
                    <span class="tub-label">Total Value</span>
                    <span class="tub-value">{{ totalUsdtStr }}</span>
                    <button class="tub-fast" @click="fastUpdatePrices" :disabled="fastUpdating || tokensLoading" title="Fast update — Binance prices only, no token rediscovery">
                        <span v-if="fastUpdating" class="spinner-border spinner-border-sm" role="status"></span>
                        <span v-else>⚡</span> Fast Update
                    </button>
                    <button class="tub-refresh" @click="loadBalances" :disabled="tokensLoading" title="Full refresh — balances + token discovery">
                        <span v-if="tokensLoading" class="spinner-border spinner-border-sm" role="status"></span>
                        <span v-else>↻</span>
                    </button>
                </div>
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>Asset</th>
                            <th>Balance</th>
                            <th>Value (USDT)</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="balances.length === 0 && !tokensLoading">
                            <td colspan="4" class="text-center text-muted">Connect Ledger to load balances</td>
                        </tr>
                        <tr v-if="tokensLoading">
                            <td colspan="4" class="text-center text-muted small py-1">
                                <span class="spinner-border spinner-border-sm mr-1" style="width:.75rem;height:.75rem"></span>
                                {{ pricingProgressMsg }}
                            </td>
                        </tr>
                        <tr v-for="b in sortedBalances" :key="b.contractAddress || b.ticker" :class="{ 'native-row': b.type === 'native' }">
                            <td>
                                <span class="ticker-badge" :class="{ 'ticker-badge-native': b.type === 'native' }">
                                    <span v-if="b.type === 'native'" class="native-dot"></span>{{ b.ticker }}
                                </span>
                            </td>
                            <td>
                                {{ b.balance }}
                                <span v-if="refreshingTicker === b.ticker" class="spinner-border spinner-border-sm ml-1" style="width:.7rem;height:.7rem"></span>
                            </td>
                            <td>{{ b.usdValue != null ? '$' + b.usdValue.toFixed(2) : priceOf(b.ticker, b.rawBalance) }}</td>
                            <td>
                                <button class="btn btn-success btn-sm" @click="openModal(b.ticker, b.balance, b.type, b.contractAddress, b.decimals)">Send</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-sm-5">
                <table class="table table-striped text-center">
                    <thead><tr><th>Name</th><th>Address</th><th>Actions</th></tr></thead>
                    <tbody>
                        <tr v-if="addresses.length === 0"><td colspan="3" class="text-muted">No saved addresses</td></tr>
                        <tr v-for="(entry, i) in addresses" :key="i">
                            <td>{{ entry.name }}</td>
                            <td class="small text-break">{{ entry.address }}</td>
                            <td>
                                <button class="btn btn-info btn-sm mx-1" @click="copyAddress(entry.address)">Copy</button>
                                <button class="btn btn-danger btn-sm" @click="deleteAddress(i)">Del</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- RPC Panel -->
        <div class="row mt-2">
            <div class="col-12">
                <div class="rpc-panel">
                    <span class="rpc-title">RPC Nodes</span>
                    <span v-for="rpc in rpcList" :key="rpc.url" class="rpc-item" :class="{ 'rpc-active': activeRpc === rpc.url }" @click="switchRpc(rpc.url)">
                        <span class="rpc-dot" :class="rpcStatus[rpc.url]"></span>{{ rpc.label }}
                    </span>
                    <span class="rpc-item" @click="pingAllRpc" style="cursor:pointer;margin-left:auto;">↻ Refresh</span>
                </div>
            </div>
        </div>

        <!-- Transaction Chart -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-toolbar">
                        <span class="chart-panel-title">Transaction History</span>
                        <div class="chart-input-group">
                            <input type="text" class="form-control form-control-sm chart-addr-input" placeholder="BSC address (0x…)" v-model="chartAddress" @keyup.enter="loadChart" />
                            <button class="btn btn-bsc btn-sm" @click="loadChart" :disabled="chartLoading">
                                <span v-if="chartLoading">Loading…</span><span v-else>Load Chart</span>
                            </button>
                            <button v-if="bnbAddress !== 'N/A'" class="btn btn-outline-secondary btn-sm" @click="chartAddress = bnbAddress; loadChart()">My Address</button>
                        </div>
                        <div class="chart-toggles">
                            <button class="btn btn-sm" :class="chartMode==='count' ? 'btn-bsc' : 'btn-outline-secondary'" @click="setChartMode('count')">TX Count</button>
                            <button class="btn btn-sm" :class="chartMode==='volume' ? 'btn-bsc' : 'btn-outline-secondary'" @click="setChartMode('volume')">BNB Volume</button>
                        </div>
                        <div class="chart-toggles">
                            <button v-for="d in [30,60,90]" :key="d" class="btn btn-sm" :class="chartDays===d ? 'btn-bsc' : 'btn-outline-secondary'" @click="chartDays=d; renderChart()">{{ d }}d</button>
                        </div>
                    </div>
                    <div class="chart-summary" v-if="chartStats.total > 0">
                        <span class="chart-stat"><strong>{{ chartStats.total }}</strong> TXs</span>
                        <span class="chart-stat"><span class="stat-in">▲ {{ chartStats.received }} BNB received</span></span>
                        <span class="chart-stat"><span class="stat-out">▼ {{ chartStats.sent }} BNB sent</span></span>
                        <span class="chart-stat text-muted">Last {{ chartDays }} days</span>
                    </div>
                    <div class="chart-wrapper">
                        <canvas ref="txChart"></canvas>
                        <div v-if="!chartLoaded && !chartLoading" class="chart-placeholder">Enter a BSC address above and click <strong>Load Chart</strong></div>
                        <div v-if="chartLoading" class="chart-placeholder">
                            <div class="spinner-border" style="color:#F0B90B" role="status"></div>
                            <p class="mt-2 mb-0">Fetching transactions from BSCScan…</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- TX Receipt Modal -->
        <Modal v-model="showReceipt" title="Transaction Sent">
            <div class="text-center p-3">
                <div style="font-size:3rem;color:#28a745">✓</div>
                <p class="mt-2">Transaction submitted to BSC network.</p>
                <input type="text" class="form-control form-control-sm mb-2" :value="txid" readonly />
                <a :href="'https://bscscan.com/tx/' + txid" target="_blank" class="btn btn-bsc btn-sm">View on BSCScan ↗</a>
            </div>
        </Modal>

        <!-- Send Modal -->
        <Modal v-model="showModal" :title="'Send ' + modalTicker">
            <form novalidate @submit.prevent="sendTransaction">
                <div class="form-group">
                    <label>From</label>
                    <input type="text" class="form-control form-control-sm text-muted" :value="bnbAddress" readonly />
                </div>
                <div class="form-group">
                    <label>To Address</label>
                    <input type="text" class="form-control" placeholder="0x..." v-model="modalToAddress" />
                </div>
                <div class="form-group">
                    <label>Amount ({{ modalTicker }})</label>
                    <div class="input-group">
                        <input type="text" class="form-control" placeholder="0.0" v-model="modalAmount" />
                        <div class="input-group-append">
                            <button type="button" class="btn btn-outline-secondary btn-sm" @click="modalAmount = modalMaxBalance">Max</button>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label>Gas Price (Gwei)</label>
                    <input type="number" class="form-control" v-model.number="gasPrice" min="1" />
                </div>
                <div class="form-group">
                    <label>Gas Limit</label>
                    <input type="number" class="form-control" v-model.number="gasLimit" min="21000" />
                </div>
                <div class="form-group small text-muted">Estimated fee: <strong>{{ estimatedFee }} BNB</strong></div>
                <div class="row modal-footer">
                    <div class="col-12">
                        <button class="btn btn-danger float-left" type="button" @click="showModal = false">Cancel</button>
                        <button class="btn btn-bsc float-right" type="submit" :disabled="sending">
                            <span v-if="sending">Sending…</span><span v-else>Send</span>
                        </button>
                    </div>
                </div>
            </form>
        </Modal>

        <!-- Add Address Modal -->
        <Modal v-model="showAddAddressModal" title="Add New Address">
            <form novalidate @submit.prevent="submitNewAddress">
                <div class="form-group">
                    <label>Name</label>
                    <input type="text" class="form-control" placeholder="Label" v-model="newAddressName" required />
                </div>
                <div class="form-group">
                    <label>BSC Address</label>
                    <input type="text" class="form-control" placeholder="0x..." v-model="newAddress" required />
                </div>
                <div class="row modal-footer">
                    <div class="col-12">
                        <button type="button" class="btn btn-danger float-left" @click="showAddAddressModal = false">Cancel</button>
                        <button type="submit" class="btn btn-bsc float-right">Save</button>
                    </div>
                </div>
            </form>
        </Modal>

        <!-- Receive Modal -->
        <ReceiveModal v-if="showReceive" :address="bnbAddress" chain="bsc" @close="showReceive = false" />

        <!-- Transaction History -->

        <!-- Address Matrix / Tree -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-panel-title mb-2">Address Overview</div>
                    <AddressMatrix
                        :cells="matrixCells"
                        :active-index="accountIndex"
                        accent-color="#F0B90B"
                        unit="BNB"
                        :decimals="6"
                        :loading="matrixLoading"
                        root-label="m/44'/60'/0'/0"
                        @refresh="loadMatrix(true)"
                        @select="onMatrixSelect" />
                </div>
            </div>
        </div>

        <WalletTxHistory chain="bsc" :address="bnbAddress" accent-color="#F0B90B" />
    </div>
</template>

<script>
import Eth from '@ledgerhq/hw-app-eth';
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend, BarController } from 'chart.js';
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, BarController);
import TransportWebHID from '@ledgerhq/hw-transport-webhid';
import TransportWebUSB from '@ledgerhq/hw-transport-webusb';
import { ethers } from 'ethers';
import numeral from 'numeral';
import BigNumber from 'bignumber.js';
import '@kouts/vue-modal/dist/vue-modal.css';
import axios from 'axios';
import { fetchPrices, formatUsd } from '../services/priceService';
import { saveAccount, getAccounts } from '../services/accountService';
import { getCustomTokens } from '../services/tokenService';
import { loadEvmTokens } from '../services/tokenLoader';
import { getAddresses, saveAddress, deleteAddress as deleteAddressPb } from '../services/pbService';
import ReceiveModal from './ReceiveModal.vue';
import AddressMatrix from './AddressMatrix.vue';
import { deriveEvmAddress, isValidHex } from '../services/evmXpub';
import eventBus from '../services/eventBus';

const EVM_NODE_KEY = 'hd_bsc_account_node';
const MATRIX_ADDRESS_COUNT = 5;

const CHAIN_ID = 56;
const ACCENT = '#F0B90B';
const NATIVE = 'BNB';
const EXPLORER = 'https://bscscan.com';
const ETHERSCAN_KEY = process.env.VUE_APP_ETHERSCAN_KEY || '';
const SCAN_API = `https://api.etherscan.io/v2/api?chainid=56&apikey=${ETHERSCAN_KEY}`;

const RPC_LIST = [
    { label: 'Binance',    url: 'https://bsc-dataseed.binance.org' },
    { label: 'PublicNode', url: 'https://bsc.publicnode.com' },
    { label: 'Ankr',       url: 'https://rpc.ankr.com/bsc' },
];

const DEFAULT_TOKENS = [
    { ticker: 'USDT',        address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
    { ticker: 'USDC',        address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18 },
    { ticker: 'BUSD',        address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', decimals: 18 },
    { ticker: 'DAI',         address: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3', decimals: 18 },
    { ticker: 'WBNB',        address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', decimals: 18 },
    { ticker: 'BTC',         address: '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c', decimals: 18 },
    { ticker: 'ETH',         address: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8', decimals: 18 },
    { ticker: 'XRP',         address: '0x1D2F0da169ceB9fC7B3144628dB156f3F6c60dBE', decimals: 18 },
    { ticker: 'LTC',         address: '0x4338665CBB7B2485A8855A139b75D5e34AB0DB94', decimals: 18 },
    { ticker: 'DOT',         address: '0x7083609fCE4d1d8Dc0C979AAb8c869Ea2C873402', decimals: 18 },
    { ticker: 'ADA',         address: '0x3EE2200Efb3400fAbB9AacF31297cBdD1d435D47', decimals: 18 },
    { ticker: 'SOL',         address: '0x570A5D26f7765Ecb712C0924E4De545B89fD43dF', decimals: 18 },
    { ticker: 'DOGE',        address: '0xbA2aE424d960c26247Dd6c32edC70B295c744C43', decimals: 8  },
    { ticker: 'AVAX',        address: '0x1CE0c2827e2eF14D5C4f29a091d735A204794041', decimals: 18 },
    { ticker: 'MATIC',       address: '0xCC42724C6683B7E57334c4E856f4c9965ED682bD', decimals: 18 },
    { ticker: 'ATOM',        address: '0x0Eb3a705fc54725037CC9e008bDede697f62F335', decimals: 18 },
    { ticker: 'LINK',        address: '0xF8A0BF9cF54Bb92F17374d9e9A321E6a111a51bD', decimals: 18 },
    { ticker: 'UNI',         address: '0xBf5140A22578168FD562DCcF235E5D43A02ce9B1', decimals: 18 },
    { ticker: 'AAVE',        address: '0xfb6115445Bff7b52FeB98650C87f44907E58f802', decimals: 18 },
    { ticker: 'CAKE',        address: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82', decimals: 18 },
    { ticker: 'SHIB',        address: '0x2859e4544C4bB03966803b044A93563Bd2D0DD4D', decimals: 18 },
    { ticker: 'BCH',         address: '0x8fF795a6F4D97E7887C79beA79aba5cc76444aDf', decimals: 18 },
    { ticker: 'ETC',         address: '0x3d6545b08693daE087E957cb1180ee38B9e3c25E', decimals: 18 },
    { ticker: 'FTM',         address: '0xAD29AbB318791D579433D831ed122aFeAf29dcfe', decimals: 18 },
    { ticker: 'AXS',         address: '0x715D400F88C167884bbCc41C5FeA407ed4D2f8A0', decimals: 18 },
    { ticker: 'SUSHI',       address: '0x947950BcC74888a40Ffa2593C5798F11Fc9124C4', decimals: 18 },
    { ticker: 'SNX',         address: '0x9Ac983826058b8a9C7Aa1C9171441191232E8404', decimals: 18 },
    { ticker: 'MKR',         address: '0x5f0Da599BB2ccCfcf6Fdfd7D81743B6020864350', decimals: 18 },
    { ticker: 'MANA',        address: '0x26433c8127d9b4e9B71Eaa15111DF99Ea2EeB2f8', decimals: 18 },
    { ticker: 'SAND',        address: '0x67b725d7e342d7B611fa85e859Df9697D9378B2e', decimals: 18 },
    { ticker: 'BAT',         address: '0x101d82428437127bF1608F699CD651e6Abf9766E', decimals: 18 },
    { ticker: 'VET',         address: '0x6FDcdfef7c496407cCb0cEC90f9C5Aaa1Cc8D888', decimals: 18 },
    { ticker: 'XLM',         address: '0x43C934A845205F0b514417d757d7235B8f53f1B9', decimals: 18 },
    { ticker: 'EOS',         address: '0x56b6fB708fC5732DEC1Afc8D8556423A2EDcCbD6', decimals: 18 },
    { ticker: 'ZEC',         address: '0x1Ba42e5193dfA8B03D15dd1B86a3113bbBEF8Eeb', decimals: 18 },
    { ticker: 'FIL',         address: '0x0D8Ce2A99Bb6e3B7Db580eD848240e4a0F9aE153', decimals: 18 },
    { ticker: 'XTZ',         address: '0x16939ef78684453bfDFb47825F8a5F714f12623a', decimals: 18 },
    { ticker: 'CELR',        address: '0x1f9f6a696C6Fd109cD3956F45dC709d2b3902163', decimals: 18 },
    { ticker: 'ONE',         address: '0x03fF0ff224f904be3118461335064bB48Df47938', decimals: 18 },
    { ticker: 'GMT',         address: '0x3019BF2a2eF8040C242C9a4c5c4BD4C81678b2A1', decimals: 8  },
    { ticker: 'FLOKI',       address: '0xfb5B838b6cfEEdC2873aB27866079AC55363D37E', decimals: 9  },
    { ticker: '1MBABYDOGE',  address: '0xc748673057861a797275CD8A068AbB95A902e8de', decimals: 9  },
    { ticker: 'TON',         address: '0x76A797A59Ba2C17726896976B7B3747BfD1d220f', decimals: 9  },
    { ticker: 'IOTA',        address: '0xd944f1D1e9d5f9Bb90b62f9D45e447D989580782', decimals: 6  },
    { ticker: 'ZIL',         address: '0xb86abcb37c3a4b64f74f59301aff131a1becc787', decimals: 12 },
    { ticker: 'INJ',         address: '0xa2B726B1145A4773F68593CF171187d8EBe4d495', decimals: 18 },
    { ticker: 'NEAR',        address: '0x1Fa4a73a3F0133f0025378af00236f3aBDEE5D63', decimals: 18 },
];

const ERC20_ABI = [
    'function balanceOf(address) view returns (uint256)',
    'function transfer(address to, uint256 amount) returns (bool)',
];

let provider = new ethers.providers.JsonRpcProvider(RPC_LIST[0].url);
let transport = null;
let ethApp = null;

export default {
    name: 'BscWallet',
    components: {
        ReceiveModal,
        Modal: () => import('@kouts/vue-modal').then(m => m.default),
        WalletTxHistory: () => import('./WalletTxHistory.vue'),
        AddressMatrix,
    },
    data() {
        return {
            bnbAddress: 'N/A',
            accountIndex: 0,
            hdPath: 'N/A',
            accountNode: null, // { pubkeyHex, chainCodeHex } at m/44'/60'/0'/0
            matrixCells: [],
            matrixLoading: false,
            balances: [],
            tokensLoading: false,
            pricingProgress: null,
            fastUpdating: false,
            refreshingTicker: null, // ticker currently being spot-refreshed (post-send)
            rpcList: RPC_LIST,
            activeRpc: RPC_LIST[0].url,
            rpcStatus: {},
            showModal: false,
            showReceipt: false,
            showReceive: false,
            modalTicker: '',
            modalToAddress: '',
            modalAmount: '',
            modalMaxBalance: '',
            modalTxType: '',
            modalContractAddress: '',
            modalDecimals: 0,
            gasPrice: 3,
            gasLimit: 21000,
            sending: false,
            txid: '',
            showAddAddressModal: false,
            newAddressName: '',
            newAddress: '',
            addresses: [],
            prices: {},
            priceTimer: null,
            chartAddress: '',
            chartMode: 'count',
            chartDays: 30,
            chartLoading: false,
            chartLoaded: false,
            chartInstance: null,
            chartStats: { total: 0, received: '0', sent: '0' },
        };
    },
    computed: {
        estimatedFee() {
            return new BigNumber(this.gasPrice).multipliedBy(this.gasLimit).div(1e9).toFixed(8);
        },
        // Native token pinned to the top row, everything else sorted by USD value descending.
        sortedBalances() {
            const native = this.balances.filter(b => b.type === 'native');
            const rest = this.balances.filter(b => b.type !== 'native');
            // While tokens are still streaming in (tokensLoading), leave them in
            // arrival order — re-sorting every incremental push would make rows
            // jump around mid-fetch. Final sort happens once pricing finishes.
            if (this.tokensLoading) return [...native, ...rest];
            return [...native, ...rest.slice().sort((a, b) => this.usdValueNum(b) - this.usdValueNum(a))];
        },
        totalUsdt() {
            return this.balances.reduce((sum, b) => sum + Math.max(this.usdValueNum(b), 0), 0);
        },
        totalUsdtStr() {
            return formatUsd(this.totalUsdt) + ' USDT';
        },
        pricingProgressMsg() {
            const p = this.pricingProgress;
            if (!p) return 'Scanning all tokens…';
            if (p.stage === 'binance') return 'Pricing via Binance…';
            if (p.stage === 'coingecko') return `Pricing ${p.current}/${p.total} tokens via CoinGecko…`;
            return 'Scanning all tokens…';
        },
    },
    methods: {
        // ── Ledger ──────────────────────────────────────────────────────
        async createTransport() {
            if (await TransportWebHID.isSupported()) return TransportWebHID.create();
            if (await TransportWebUSB.isSupported()) return TransportWebUSB.create();
            throw new Error('WebHID/WebUSB not supported. Please use Chrome or Edge over HTTPS.');
        },

        async onConnectLedger(e) {
            if (e) e.preventDefault();
            this.notice('info', 'Connecting', 'Opening Ledger connection…', 3);
            try {
                if (transport) await transport.close().catch(() => {});
                transport = await this.createTransport();
                ethApp = new Eth(transport);
                const path = `44'/60'/0'/0/${this.accountIndex}`;
                const { address } = await ethApp.getAddress(path, false, true);
                this.bnbAddress = address;
                this.hdPath = 'm/' + path;
                this.notice('success', 'Connected', 'Address loaded.');
                await this.loadBalances();
                await this.fetchGasPrice();
                await this.refreshPrices();
                // Save account
                const accounts = saveAccount({ chain: 'bsc', hdIndex: this.accountIndex, address, label: `BSC #${this.accountIndex}` });
                if (this.$store) this.$store.commit('accounts/SET_ACCOUNTS', accounts);

                // Cache the account-level pubkey/chaincode (m/44'/60'/0'/0) so the
                // Address Matrix/Tree can derive other indices without reconnecting.
                try {
                    const { publicKey, chainCode } = await ethApp.getAddress("44'/60'/0'/0", false, true);
                    if (isValidHex(publicKey) && isValidHex(chainCode)) {
                        this.accountNode = { pubkeyHex: publicKey, chainCodeHex: chainCode };
                        localStorage.setItem(EVM_NODE_KEY, JSON.stringify(this.accountNode));
                    }
                } catch (nodeErr) {
                    console.error('Failed to cache bsc account node for matrix/tree:', nodeErr);
                }
                await this.loadMatrix();
            } catch (err) {
                const msg = err.message || String(err);
                const friendly = msg.includes('0x6e00') || msg.includes('CLA_NOT_SUPPORTED')
                    ? 'Open the Ethereum app on your Ledger, then try again. BSC uses the Ethereum Ledger app.'
                    : msg;
                this.notice('error', 'Ledger Error', friendly, 10);
            }
        },

        // ── Address Matrix / Tree ──────────────────────────────────────────
        async loadMatrix(notifyIfEmpty = false) {
            const cells = [];
            const savedAccounts = getAccounts().filter(a => a.chain === 'bsc');
            for (let idx = 0; idx < MATRIX_ADDRESS_COUNT; idx++) {
                let address = null;
                if (this.accountNode) {
                    try {
                        address = deriveEvmAddress(this.accountNode.pubkeyHex, this.accountNode.chainCodeHex, idx);
                    } catch (e) {
                        console.error(`Failed to derive bsc address for index ${idx}:`, e);
                    }
                } else {
                    const saved = savedAccounts.find(a => a.hdIndex === idx);
                    if (saved) address = saved.address;
                }
                if (address) cells.push({ index: idx, address, balance: null, loading: true });
            }
            this.matrixCells = cells;
            if (!cells.length) {
                if (notifyIfEmpty) {
                    this.notice('error', 'Address Matrix Empty', 'No cached addresses/xpub found — reconnect your Ledger to populate it.', 8);
                }
                return;
            }
            this.matrixLoading = true;
            const results = await Promise.allSettled(
                cells.map(async c => ({ index: c.index, wei: await provider.getBalance(c.address) }))
            );
            const byIndex = {};
            for (const r of results) {
                if (r.status === 'fulfilled') byIndex[r.value.index] = parseFloat(ethers.utils.formatEther(r.value.wei));
                else console.error('[bsc matrix] balance fetch rejected:', r.reason);
            }
            this.matrixCells = this.matrixCells.map(c => ({ ...c, balance: byIndex[c.index] ?? 0, loading: !(c.index in byIndex) }));
            this.matrixLoading = false;
        },

        onMatrixSelect(idx) {
            const cell = this.matrixCells.find(c => c.index === idx);
            if (!cell) return;
            this.accountIndex = idx;
            this.bnbAddress = cell.address;
            this.hdPath = `m/44'/60'/0'/0/${idx}`;
            this.loadBalances();
        },

        async loadBalances() {
            this.balances = [];
            try {
                const wei = await provider.getBalance(this.bnbAddress);
                const bal = ethers.utils.formatEther(wei);
                this.balances.push({ ticker: NATIVE, balance: numeral(parseFloat(bal)).format('0,0.000000000'), type: 'native', rawBalance: bal, contractAddress: null, decimals: 18 });
            } catch (e) { this.notice('error', 'Balance Error', e.message, 8); }

            this.tokensLoading = true;
            this.pricingProgress = null;
            // Clear any stale erc20 rows first, then let onToken() push each freshly
            // priced token in as soon as it resolves — visible immediately, but left
            // unsorted (see sortedBalances) until pricing finishes below.
            this.balances = this.balances.filter(b => b.type !== 'erc20');
            loadEvmTokens(this.bnbAddress, 56, provider, 'bsc', DEFAULT_TOKENS,
                p => { this.pricingProgress = p; },
                entry => {
                    const idx = this.balances.findIndex(b => b.type === 'erc20' && b.contractAddress === entry.contractAddress);
                    if (idx >= 0) this.balances.splice(idx, 1, entry);
                    else this.balances.push(entry);
                })
                .then(tokens => {
                    // Authoritative final set — replaces the optimistic incremental
                    // rows (a token can drop out here if it ended up under the
                    // $0.01 display threshold once fully priced).
                    this.balances = this.balances.filter(b => b.type !== 'erc20');
                    this.balances.push(...tokens);
                    if (this.$store) this.$store.commit('wallets/SET_WALLET', { chain: 'bsc', data: { address: this.bnbAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
                })
                .catch(e => this.notice('error', 'Token Load Error', e.message, 6))
                .finally(() => { this.tokensLoading = false; this.pricingProgress = null; });

            this.notice('success', 'Balances', 'BNB loaded — tokens loading…', 2);
        },

        async fetchGasPrice() {
            try {
                const gp = await provider.getGasPrice();
                this.gasPrice = Math.ceil(parseFloat(ethers.utils.formatUnits(gp, 'gwei')));
            } catch {}
        },

        async refreshPrices() {
            const tickers = this.balances.map(b => b.ticker);
            const { prices } = await fetchPrices(tickers);
            this.prices = prices;
            if (this.$store) this.$store.dispatch('prices/fetchPrices', tickers);
        },

        // "Fast Update" — Binance ticker prices only, no balanceOf re-fetch and no
        // token rediscovery/CoinGecko pass, so it's near-instant even with a long
        // token list. Only repriced tokens whose contract address we trust (native
        // BNB + the hardcoded default list) — same rule as loadEvmTokens: a
        // ticker string alone can be spoofed by a discovered/unverified token.
        async fastUpdatePrices() {
            if (!this.balances.length) return;
            this.fastUpdating = true;
            try {
                const tickers = this.balances.map(b => b.ticker);
                const { prices } = await fetchPrices(tickers, true);
                this.prices = prices;
                this.balances = this.balances.map(b => {
                    const isTrusted = b.type === 'native'
                        || DEFAULT_TOKENS.some(t => t.address.toLowerCase() === (b.contractAddress || '').toLowerCase());
                    if (!isTrusted || prices[b.ticker] === undefined) return b;
                    const raw = parseFloat(String(b.rawBalance).replace(/,/g, '')) || 0;
                    return { ...b, price: prices[b.ticker], usdValue: raw * prices[b.ticker] };
                });
                if (this.$store) this.$store.commit('wallets/SET_WALLET', { chain: 'bsc', data: { address: this.bnbAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
                this.notice('success', 'Fast Update', 'USDT prices refreshed from Binance.', 2);
            } catch (e) {
                this.notice('error', 'Fast Update Failed', e.message, 5);
            } finally {
                this.fastUpdating = false;
            }
        },

        // Refresh only the single coin that was just sent — re-fetches its on-chain
        // balance and (for trusted/native tokens) its Binance price, instead of a
        // full loadBalances() reload of the whole token list.
        async refreshOneBalance(ticker, type, contractAddress, decimals) {
            const idx = this.balances.findIndex(b => b.ticker === ticker && b.type === type);
            if (idx === -1) return;
            this.refreshingTicker = ticker;
            try {
                let rawBalance, balanceStr;
                if (type === 'native') {
                    const wei = await provider.getBalance(this.bnbAddress);
                    rawBalance = parseFloat(ethers.utils.formatEther(wei));
                    balanceStr = numeral(rawBalance).format('0,0.000000000');
                } else {
                    const c = new ethers.Contract(contractAddress, ERC20_ABI, provider);
                    const raw = await c.balanceOf(this.bnbAddress);
                    const val = new BigNumber(raw.toString()).div(new BigNumber(10).pow(decimals));
                    rawBalance = parseFloat(val.toFixed(decimals));
                    balanceStr = val.toFixed(Math.min(decimals, 6));
                }
                const isTrusted = type === 'native'
                    || DEFAULT_TOKENS.some(t => t.address.toLowerCase() === (contractAddress || '').toLowerCase());
                let price = this.balances[idx].price ?? 0;
                if (isTrusted) {
                    const { prices } = await fetchPrices([ticker], true);
                    if (prices[ticker] !== undefined) price = prices[ticker];
                }
                this.balances.splice(idx, 1, { ...this.balances[idx], balance: balanceStr, rawBalance, price, usdValue: rawBalance * price });
                if (this.$store) this.$store.commit('wallets/SET_WALLET', { chain: 'bsc', data: { address: this.bnbAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
            } catch (e) {
                console.warn('refreshOneBalance failed:', e.message);
            } finally {
                this.refreshingTicker = null;
            }
        },

        priceOf(ticker, rawBalance) {
            const raw = parseFloat(String(rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[ticker] ?? null;
            if (price === null) return '—';
            const val = raw * price;
            return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        // Numeric USD value for sorting — falls back to live price lookup like priceOf(),
        // but returns -1 (sorts last) when no value can be determined at all.
        usdValueNum(b) {
            if (b.usdValue != null) return b.usdValue;
            const raw = parseFloat(String(b.rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[b.ticker] ?? null;
            return price === null ? -1 : raw * price;
        },

        // ── Send ────────────────────────────────────────────────────────
        openModal(ticker, balance, type, contractAddress, decimals) {
            this.showModal = true;
            this.modalTicker = ticker;
            this.modalToAddress = '';
            this.modalAmount = '';
            this.modalMaxBalance = balance.replace(/,/g, '');
            this.modalTxType = type;
            this.modalContractAddress = contractAddress || '';
            this.modalDecimals = decimals || 18;
            this.gasLimit = type === 'erc20' ? 65000 : 21000;
            this.txid = '';
            this.fetchGasPrice();
        },

        async sendTransaction() {
            if (!ethers.utils.isAddress(this.modalToAddress)) {
                this.notice('error', 'Invalid Address', 'Enter a valid BSC address.', 5); return;
            }
            if (!this.modalAmount || parseFloat(this.modalAmount) <= 0) {
                this.notice('error', 'Invalid Amount', 'Amount must be > 0.', 5); return;
            }
            this.sending = true;
            const path = `44'/60'/0'/0/${this.accountIndex}`;
            try {
                const nonce = await provider.getTransactionCount(this.bnbAddress, 'latest');
                const gasPriceWei = ethers.utils.parseUnits(String(this.gasPrice), 'gwei');
                // Use EIP-1559 type-2 transactions: Ledger Ethereum app returns raw parity (0/1)
                // for type-2, avoiding the "Invalid v value" error that occurs with legacy type-0
                // on non-mainnet chains where the device doesn't apply EIP-155 v encoding.
                let txData;
                if (this.modalTxType === 'native') {
                    txData = { type: 2, to: this.modalToAddress, value: ethers.utils.parseEther(String(this.modalAmount)).toHexString(), maxFeePerGas: gasPriceWei.mul(2).toHexString(), maxPriorityFeePerGas: gasPriceWei.toHexString(), gasLimit: ethers.BigNumber.from(this.gasLimit).toHexString(), nonce: ethers.utils.hexlify(nonce), chainId: CHAIN_ID, accessList: [], data: '0x' };
                } else {
                    const iface = new ethers.utils.Interface(ERC20_ABI);
                    const amount = new BigNumber(this.modalAmount).multipliedBy(new BigNumber(10).pow(this.modalDecimals)).toFixed(0);
                    txData = { type: 2, to: this.modalContractAddress, value: '0x0', maxFeePerGas: gasPriceWei.mul(2).toHexString(), maxPriorityFeePerGas: gasPriceWei.toHexString(), gasLimit: ethers.BigNumber.from(this.gasLimit).toHexString(), nonce: ethers.utils.hexlify(nonce), chainId: CHAIN_ID, accessList: [], data: iface.encodeFunctionData('transfer', [this.modalToAddress, ethers.BigNumber.from(amount)]) };
                }
                const unsignedTx = ethers.utils.serializeTransaction(txData);
                this.notice('info', 'Confirm on Ledger', 'Approve on your Ledger device.', 60);
                const sig = await ethApp.signTransaction(path, unsignedTx.slice(2), null);
                const _sigToHex = (val) => {
                    if (Buffer.isBuffer(val)) return ethers.utils.hexlify(val);
                    if (typeof val === 'string' && val.includes(',')) return ethers.utils.hexlify(Buffer.from(val.split(',').map(Number)));
                    if (typeof val === 'string') return ethers.utils.hexlify(Buffer.from(val, 'hex'));
                    return ethers.utils.hexlify(val);
                };
                const rHex = _sigToHex(sig.r);
                const sHex = _sigToHex(sig.s);
                // For type-2, recover y-parity (0 or 1) by checking which candidate recovers our address
                const txHash = ethers.utils.keccak256(unsignedTx);
                let vInt = 0;
                for (const candidate of [0, 1]) {
                    try {
                        const recovered = ethers.utils.recoverAddress(txHash, { r: rHex, s: sHex, v: candidate });
                        if (recovered.toLowerCase() === this.bnbAddress.toLowerCase()) { vInt = candidate; break; }
                    } catch {}
                }
                const signedTx = ethers.utils.serializeTransaction(txData, { v: vInt, r: rHex, s: sHex });
                const response = await provider.sendTransaction(signedTx);
                this.txid = response.hash;
                this.showModal = false;
                this.showReceipt = true;
                this.notice('success', 'Sent!', `TX: ${response.hash.slice(0, 16)}…`, 5);
                // Only re-fetch the sent coin's own balance/price, not a full reload
                // of every token — sending BNB or one BEP20 doesn't change what any
                // other token's balance or price is.
                const sentTicker = this.modalTicker, sentType = this.modalTxType, sentContract = this.modalContractAddress, sentDecimals = this.modalDecimals;
                response.wait(1).then(() => this.refreshOneBalance(sentTicker, sentType, sentContract, sentDecimals)).catch(() => {});
            } catch (err) {
                this.notice('error', 'Transaction Error', err.message || String(err), 10);
            } finally { this.sending = false; }
        },

        // ── RPC ─────────────────────────────────────────────────────────
        async pingRpc(url) {
            this.$set(this.rpcStatus, url, 'checking');
            try {
                const p = new ethers.providers.JsonRpcProvider(url);
                await p.getBlockNumber();
                this.$set(this.rpcStatus, url, 'ok');
            } catch { this.$set(this.rpcStatus, url, 'error'); }
        },

        switchRpc(url) {
            this.activeRpc = url;
            provider = new ethers.providers.JsonRpcProvider(url);
            this.pingRpc(url);
        },

        pingAllRpc() { this.rpcList.forEach(r => this.pingRpc(r.url)); },

        // ── Address Book ────────────────────────────────────────────────
        async fetchAddresses() {
            try {
                const res = await getAddresses();
                this.addresses = res.items
                    .filter(r => r.chain === 'bsc' || r.chain === 'all')
                    .map(r => ({ id: r.id, name: r.label, address: r.address }));
            } catch (e) {
                console.error('Failed to load addresses:', e);
            }
        },

        async submitNewAddress() {
            if (!ethers.utils.isAddress(this.newAddress)) { alert('Enter a valid BSC address.'); return; }
            try {
                await saveAddress({
                    label: this.newAddressName,
                    address: this.newAddress,
                    chain: 'bsc',
                    notes: '',
                    aml_risk_level: 'unknown',
                });
                await this.fetchAddresses();
            } catch (e) {
                alert('Failed to add address: ' + (e?.message || e));
                return;
            }
            this.newAddressName = '';
            this.newAddress = '';
            this.showAddAddressModal = false;
        },

        async deleteAddress(index) {
            const entry = this.addresses[index];
            try {
                if (entry.id) await deleteAddressPb(entry.id);
                this.addresses.splice(index, 1);
            } catch (e) {
                alert('Delete failed: ' + (e?.message || e));
            }
        },

        copyAddress(address) {
            navigator.clipboard.writeText(address).then(() => this.notice('success', 'Copied', 'Address copied.', 2));
        },

        logout() { localStorage.removeItem('isLoggedIn'); window.location.reload(); },

        notice(type, title, description, duration) {
            this.$notice[type]({ title, description: description || '', duration: duration ?? 1 });
        },

        // ── Chart ────────────────────────────────────────────────────────
        setChartMode(mode) {
            this.chartMode = mode;
            if (this.chartLoaded) this.renderChart();
        },

        async loadChart() {
            const addr = this.chartAddress.trim();
            if (!ethers.utils.isAddress(addr)) { this.notice('error', 'Invalid Address', 'Enter a valid 0x BSC address.', 4); return; }
            this.chartLoading = true;
            this.chartLoaded = false;
            try {
                const url = `${SCAN_API}&module=account&action=txlist&address=${addr}&sort=desc&page=1&offset=200&startblock=0&endblock=99999999`;
                const res = await axios.get(url, { timeout: 15000 });
                const txs = Array.isArray(res.data.result) ? res.data.result : [];
                const now = Date.now();
                const cutoff = now - this.chartDays * 86400000;
                const countMap = {}, sentMap = {}, recvMap = {};
                for (let d = 0; d < this.chartDays; d++) {
                    const day = this._dayKey(now - d * 86400000);
                    countMap[day] = 0; sentMap[day] = 0; recvMap[day] = 0;
                }
                let totalCount = 0, totalSent = 0, totalRecv = 0;
                txs.forEach(tx => {
                    if (tx.isError === '1') return;
                    const ts = parseInt(tx.timeStamp) * 1000;
                    if (ts < cutoff) return;
                    const day = this._dayKey(ts);
                    if (!(day in countMap)) return;
                    countMap[day]++;
                    totalCount++;
                    const val = parseFloat(ethers.utils.formatEther(tx.value || '0'));
                    if (tx.from.toLowerCase() === addr.toLowerCase()) { sentMap[day] += val; totalSent += val; }
                    if (tx.to.toLowerCase() === addr.toLowerCase()) { recvMap[day] += val; totalRecv += val; }
                });
                this.chartStats = { total: totalCount, sent: totalSent.toFixed(6), received: totalRecv.toFixed(6) };
                this._chartData = { labels: Object.keys(countMap).sort(), countMap, sentMap, recvMap };
                this.chartLoaded = true;
                this.$nextTick(() => this.renderChart());
            } catch (e) {
                this.notice('error', 'Chart Error', e.message || String(e), 10);
            } finally { this.chartLoading = false; }
        },

        _dayKey(ms) {
            const d = new Date(ms);
            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        },

        renderChart() {
            if (!this._chartData) return;
            const { labels, countMap, sentMap, recvMap } = this._chartData;
            if (this.chartInstance) { this.chartInstance.destroy(); this.chartInstance = null; }
            const ctx = this.$refs.txChart;
            if (!ctx) return;
            const shortLabels = labels.map(l => l.slice(5));
            let datasets;
            if (this.chartMode === 'count') {
                datasets = [{ label: 'TX Count', data: labels.map(l => countMap[l]), backgroundColor: 'rgba(240,185,11,0.75)', borderColor: ACCENT, borderWidth: 1, borderRadius: 3 }];
            } else {
                datasets = [
                    { label: 'BNB Received', data: labels.map(l => parseFloat(recvMap[l].toFixed(6))), backgroundColor: 'rgba(40,167,69,0.75)', borderColor: '#28a745', borderWidth: 1, borderRadius: 3 },
                    { label: 'BNB Sent',     data: labels.map(l => parseFloat(sentMap[l].toFixed(6))), backgroundColor: 'rgba(240,185,11,0.65)', borderColor: ACCENT, borderWidth: 1, borderRadius: 3 },
                ];
            }
            this.chartInstance = new Chart(ctx, {
                type: 'bar',
                data: { labels: shortLabels, datasets },
                options: { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'top' } }, scales: { x: { stacked: this.chartMode === 'volume', grid: { display: false }, ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 15 } }, y: { stacked: this.chartMode === 'volume', beginAtZero: true } } },
            });
        },
    },
    async mounted() {
        await this.fetchAddresses();
        this.pingAllRpc();
        this.priceTimer = setInterval(() => this.refreshPrices(), 60000);
        eventBus.$on('refresh-prices', this.refreshPrices);
        // Restore active account
        const active = localStorage.getItem('hd_active_bsc');
        if (active) {
            const accts = JSON.parse(localStorage.getItem('hd_accounts') || '[]');
            const a = accts.find(x => x.id === active);
            if (a) this.accountIndex = a.hdIndex;
        }

        try {
            const cached = JSON.parse(localStorage.getItem(EVM_NODE_KEY) || 'null');
            if (cached && isValidHex(cached.pubkeyHex) && isValidHex(cached.chainCodeHex)) {
                this.accountNode = cached;
            }
        } catch (e) {
            console.error('Failed to load cached bsc account node:', e);
        }
        this.loadMatrix();
    },
    beforeDestroy() { clearInterval(this.priceTimer); eventBus.$off('refresh-prices', this.refreshPrices); if (this.chartInstance) this.chartInstance.destroy(); },
};
</script>

<style scoped>
/* ── BSC brand ── */
.btn-bsc { background: rgba(240,185,11,0.18) !important; border: 1px solid rgba(240,185,11,0.5) !important; color: #fcd34d !important; font-weight: 600 !important; }
.btn-bsc:hover, .btn-bsc:focus { background: rgba(240,185,11,0.32) !important; color: #fff !important; }
.table thead th { background: rgba(240,185,11,0.12) !important; color: #fcd34d !important; border-bottom: 2px solid rgba(240,185,11,0.35) !important; }
/* Neutral badge for regular tokens — gold is reserved for the native BNB row below */
.ticker-badge { display: inline-block; background: var(--surface-3, #1d2235); color: var(--text-2, #8892b0); font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 8px; border: 1px solid var(--border-2, #2e3650); }

/* Native BNB row — color-coded gold to stand out from ERC20 tokens */
.native-row { background: rgba(240,185,11,0.05) !important; box-shadow: inset 3px 0 0 #F0B90B; }
.ticker-badge-native { background: rgba(240,185,11,0.18); color: #fcd34d; border-color: rgba(240,185,11,0.45); }
.native-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: #F0B90B; margin-right: 5px; box-shadow: 0 0 4px rgba(240,185,11,0.7); }
.rpc-panel { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 10px 14px; background: var(--surface, #111520); border-radius: 10px; border: 1px solid var(--border, #242a3a); margin-bottom: 4px; }
.rpc-title { font-size: 0.7rem; font-weight: 700; color: var(--text-muted, #52597a); text-transform: uppercase; letter-spacing: 0.07em; margin-right: 4px; }
.rpc-item { display: flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 500; padding: 4px 11px; border-radius: 20px; cursor: pointer; border: 1px solid var(--border, #242a3a); background: var(--surface-2, #171b28); color: var(--text, #c8cfe8); transition: all 0.18s; }
.rpc-item:hover { border-color: #F0B90B; background: rgba(240,185,11,0.08); }
.rpc-active { border-color: #F0B90B !important; background: rgba(240,185,11,0.14) !important; color: #fff !important; font-weight: 700 !important; }
.rpc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--border-2, #2e3650); display: inline-block; flex-shrink: 0; }
.rpc-dot.ok { background: #22c55e; } .rpc-dot.error { background: #f43f5e; } .rpc-dot.checking { background: #f59e0b; }
.chart-panel { border: 1px solid var(--border, #242a3a); border-radius: 12px; padding: 18px; background: var(--surface, #111520); box-shadow: 0 4px 24px rgba(0,0,0,0.4); }
.chart-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.chart-panel-title { font-weight: 700; font-size: 0.86rem; color: var(--text, #c8cfe8); white-space: nowrap; margin-right: 6px; }
.chart-input-group { display: flex; gap: 6px; flex: 1; min-width: 200px; }
.chart-addr-input { flex: 1; font-size: 0.82rem; }
.chart-toggles { display: flex; gap: 4px; }
.chart-summary { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 10px; font-size: 0.8rem; padding: 8px 12px; background: var(--surface-2, #171b28); border-radius: 8px; }
.chart-stat { display: flex; align-items: center; gap: 4px; }
.stat-in { color: #22c55e; font-weight: 700; } .stat-out { color: #F0B90B; font-weight: 700; }
.chart-wrapper { position: relative; height: 280px; }
.chart-placeholder { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--text-muted, #52597a); font-size: 0.88rem; background: rgba(11,13,20,0.88); border-radius: 10px; }
</style>
